import React, { useState, useEffect, useRef } from 'react';
import { 
  Container,
  Box,
  Typography,
  Button,
  IconButton,
  Alert,
  Snackbar
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { Register, sendEmailCodePreRegister } from '../../api/modules/auth';
import { showMessage } from '../../utils/index';
import TopBar from '../../components/TopBar';

const VerificationCode = () => {
  const { loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { email, registerData } = location.state || {};
  
  const [code, setCode] = useState(['','','','','','']);
  const [timer, setTimer] = useState(60);
  const [isActive, setIsActive] = useState(true);
  const [errorState, setErrorState] = useState('');
  const inputRefs = useRef([]);

  // 验证码输入处理
  const handleCodeChange = (index, value) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newCode = [...code];
      newCode[index] = value;
      setCode(newCode);
      
      // 自动聚焦下一个输入框
      if (value && index < 5) {
        inputRefs.current[index + 1].focus();
      }
    }
  };

  // 处理退格键
  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  // 倒计时处理
  useEffect(() => {
    let interval = null;
    if (isActive && timer > 0) {
      interval = setInterval(() => {
        setTimer(timer => timer - 1);
      }, 1000);
    } else if (timer === 0) {
      setIsActive(false);
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [isActive, timer]);

  // 重发验证码
  const handleResend = async () => {
    try {
      await sendEmailCodePreRegister(registerData);
      setTimer(60);
      setIsActive(true);
    } catch (error) {
      setErrorState(error.message);
    }
  };

  // 验证码是否填写完整
  const isCodeComplete = code.every(digit => digit !== '');

  // 处理注册提交
  const handleSubmit = async () => {
    if (!isCodeComplete) return;

    try {
      const verificationCode = code.join('');
      await Register({
        ...registerData,
        code: verificationCode
      },(res)=>{
        const { code, data, msg } = res;
        if(code == 200){
          showMessage('Register successfully', 'success');
          setTimeout(()=>{
            const searchParams = new URLSearchParams(window.location.search);
            const isInvitedUser = searchParams.has('v') && searchParams.get('v') === 'isInvitedUser';

            if (isInvitedUser) {
              navigate('/campaigns/invitations/login?v=isInvitedUser',{state:{from:location}});
            }  else {
              navigate('/campaigns/invitations/login',{state:{from:location}});
            }


            
          },2000);
        }else{
          setErrorState(msg);
        }
      });
    } catch (error) {
      setErrorState(error.message);
    }
  };

  return (
    <>
      <Box sx={{ pt: { xs: '56px', md: '64px' } }}>
        <Container 
          maxWidth="sm" 
          sx={{
            minHeight: '100vh',
            bgcolor: '#fff',
            pt: 2,
            px: 2
          }}
        >
          {/* 顶部导航 */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
            mb: 3
          }}>
            <IconButton 
              onClick={() => navigate(-1)}
              sx={{ p: 0 }}
            >
              <ArrowBack sx={{ fontSize: 22, color: '#007AFF' }} />
            </IconButton>
            <Typography 
              variant="h6" 
              sx={{ 
                flex: 1,
                textAlign: 'center',
                fontSize: 17,
                fontWeight: 600,
                mr: 4
              }}
            >
              Enter Verification Code
            </Typography>
          </Box>

          {/* 验证码说明 */}
          <Box sx={{ mb: 3 }}>
            <Typography 
              sx={{ 
                fontSize: 17,
                fontWeight: 500,
                mb: 1,
                textAlign: 'center',
                color: '#000'
              }}
            >
              We have sent a verification code to
            </Typography>
            <Typography
              sx={{
                fontSize: 17,
                fontWeight: 500,
                mb: 1,
                textAlign: 'center',
                color: '#000'
              }}
            >
              {email}
            </Typography>
            <Typography 
              sx={{ 
                color: '#666',
                fontSize: 15,
                textAlign: 'center',
                lineHeight: 1.4,
                mt: 2
              }}
            >
              Please check your email inbox for a welcome message from noreply@uTaxes.com, Your verification code is valid for 30 minutes.
            </Typography>
          </Box>

          {/* 验证码输入框 */}
          <Typography sx={{ mb: 2, fontSize: 15, color: '#666' }}>
            Code from email
          </Typography>
          <Box 
            sx={{ 
              display: 'flex',
              justifyContent: 'space-between',
              mb: 3,
              px: 1
            }}
          >
            {[0,1,2,3,4,5].map((index) => (
              <input
                key={index}
                ref={el => inputRefs.current[index] = el}
                type="text"
                maxLength={1}
                value={code[index]}
                onChange={(e) => handleCodeChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                style={{
                  width: '45px',
                  height: '45px',
                  fontSize: '20px',
                  textAlign: 'center',
                  border: '1px solid #E5E7EB',
                  borderRadius: '12px',
                  backgroundColor: '#F5F5F8',
                  outline: 'none',
                  caretColor: '#007AFF'
                }}
              />
            ))}
          </Box>

          {/* 重发验证码 */}
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Button
              onClick={handleResend}
              disabled={isActive || loading}
              sx={{
                color: isActive ? '#666' : '#007AFF',
                fontSize: 15,
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: 'transparent'
                }
              }}
            >
              {loading ? 'Processing...' : (isActive ? `Resend verification code(${timer})` : 'Resend verification code')}
            </Button>
          </Box>

          {/* 添加错误提示 */}
          <Snackbar 
            open={!!errorState} 
            autoHideDuration={6000} 
            onClose={() => setErrorState('')}
          >
            <Alert severity="error" onClose={() => setErrorState('')}>
              {errorState}
            </Alert>
          </Snackbar>

          {/* 下一步按钮 */}
          <Button
            fullWidth
            variant="contained"
            disabled={!isCodeComplete || loading}
            onClick={handleSubmit}
            sx={{
              bgcolor: isCodeComplete ? '#007AFF' : '#E5E7EB',
              borderRadius: '14px',
              textTransform: 'none',
              fontSize: 17,
              fontWeight: 600,
              height: '50px',
              boxShadow: 'none',
              '&:hover': {
                bgcolor: isCodeComplete ? '#0066DD' : '#E5E7EB',
                boxShadow: 'none'
              },
              '&.Mui-disabled': {
                bgcolor: '#E5E7EB',
                color: '#fff'
              }
            }}
          >
            {loading ? 'Processing...' : 'Next'}
          </Button>
        </Container>
      </Box>
    </>
  );
};

export default VerificationCode;
